// @ts-nocheck: converted from JS

import CancelledTable from '@/components/Dashboard/CancelledTable';
import Button from '@/components/UI/Button';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import InVivoError from '@/model/InVivoError.ts';
import { useFetchCollection } from '@/support/Hooks/fetch/index';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import useSelectStudyCreationFormModal from '@/support/Hooks/study/useSelectStudyCreationFormModal';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ApiService } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useEffect, useState } from 'react';
import ActiveTable from './ActiveTable';
import ArchiveTable from './ArchiveTable';
import DraftsTable from './DraftsTable';
import Header from '@/components/UI/Header';

const Dashboard = () => {
  const [studyTypes, setStudyTypes] = useState<Array<unknown> | null>(null);
  const [hasStudyCode, setHasStudyCode] = useState<boolean | undefined>(undefined);
  const { openSelectStudyCreationFormModal } = useSelectStudyCreationFormModal();

  const { collection: studyMetadataFilters } = useFetchCollection({
    queryParams: {
      filter_type: 'study_meta',
    },
    collectionType: 'metadataGlossary',
  });

  useEffect(() => {
    const abortController = new AbortController();
    getStudyTypes(abortController.signal);
    checkForStudyCodes(abortController.signal);
    return () => abortController.abort('component unmounted');
  }, []);

  const checkForStudyCodes = async (signal: AbortSignal) => {
    try {
      const response = await ApiService.call({
        endpoint: 'GET /api/v1/team/study-codes',
        query: { perPage: 1 },
        signal,
      });
      setHasStudyCode(response.type === 'success' && (response.body?.data?.length ?? 0) > 0);
    } catch (error) {
      if (notAborted(error)) {
        setHasStudyCode(false);
        ExceptionHandler.captureException(
          new InVivoError('Could not check for study codes', { cause: error, slug: 'dashboard-check-study-codes' })
        );
      }
    }
  };

  const getStudyTypes = async (signal: AbortSignal) => {
    try {
      const {
        data: { data },
      } = await Http.get(apiRoute('study.presets'), {
        signal,
      });
      setStudyTypes(data);
    } catch (error) {
      if (notAborted(error)) {
        setStudyTypes([]);
        ExceptionHandler.captureException(
          new InVivoError('Could not load study presets', { cause: error, slug: 'dashboard-load-presets' })
        );
      }
    }
  };

  return (
    <>
      <div className="ph4 pb3">
        <Header
          mainHeaderText="Studies"
          asideComponent={() => (
            <Button onClick={openSelectStudyCreationFormModal} testId="create-new-study-button">
              Create new study
            </Button>
          )}
        />
      </div>
      <Tabs>
        <div className="bb b--moon-gray mh3 mh4-l">
          <Tab className="f4">Active</Tab>
          <Tab className="f4">Drafts</Tab>
          <Tab className="f4">Completed</Tab>
          <Tab className="f4">Cancelled</Tab>
        </div>
        <Panel>
          <ActiveTable
            studyTypes={studyTypes}
            studyMetadataFilters={studyMetadataFilters}
            hasStudyCode={hasStudyCode}
          />
        </Panel>
        <Panel>
          <DraftsTable studyTypes={studyTypes} studyMetadataFilters={studyMetadataFilters} />
        </Panel>
        <Panel>
          <ArchiveTable
            studyTypes={studyTypes}
            studyMetadataFilters={studyMetadataFilters}
            hasStudyCode={hasStudyCode}
          />
        </Panel>
        <Panel>
          <CancelledTable
            studyTypes={studyTypes}
            studyMetadataFilters={studyMetadataFilters}
            hasStudyCode={hasStudyCode}
          />
        </Panel>
      </Tabs>
    </>
  );
};

export default Dashboard;
